import React, { useCallback, useState, useEffect } from 'react';
import { useTick } from '@pixi/react'; 
import { Graphics as PixiGraphicsClass } from 'pixi.js'; 
import type { GameState } from './GameComponent';

interface BackgroundComponentProps {
  screenWidth: number;
  screenHeight: number;
  gameState?: GameState; 
}

// Scroll Speeds
const SCROLL_SPEED_DISTANT = 0.2; 
const SCROLL_SPEED_CLOUDS = 0.6;  
const SCROLL_SPEED_GROUND = 0.8;

// Colors
const SKY_COLOR = 0x4A90E2; 
const MOUNTAIN_COLOR_FAR = 0x2C3E50; 
const MOUNTAIN_COLOR_NEAR = 0x34495E; 
const CLOUD_COLOR_1 = 0xECF0F1; 
const CLOUD_COLOR_2 = 0xD2D7D3; 
const GROUND_COLOR_BASE = 0x8B5A2B; 
const GROUND_COLOR_LINE = 0x795548; 

const BackgroundComponent: React.FC<BackgroundComponentProps> = ({
  screenWidth,
  screenHeight,
  gameState,
}) => {
  const [distantLayerX_A, setDistantLayerX_A] = useState(0);
  const [distantLayerX_B, setDistantLayerX_B] = useState(screenWidth);
  const [cloudLayerX_A, setCloudLayerX_A] = useState(0);
  const [cloudLayerX_B, setCloudLayerX_B] = useState(screenWidth);
  const [groundLayerX_A, setGroundLayerX_A] = useState(0); 
  const [groundLayerX_B, setGroundLayerX_B] = useState(screenWidth); 

  useEffect(() => {
    if (gameState === 'menu' || gameState === 'playing') { 
      setDistantLayerX_A(0);
      setDistantLayerX_B(screenWidth);
      setCloudLayerX_A(0);
      setCloudLayerX_B(screenWidth);
      setGroundLayerX_A(0); 
      setGroundLayerX_B(screenWidth); 
    }
  }, [gameState, screenWidth]);

  useTick((ticker) => {
    if (gameState !== 'playing' && gameState !== 'gameOver') return; 

    const delta = ticker.deltaTime;
    
    const scrollDistant = SCROLL_SPEED_DISTANT * delta;
    let newDistantX_A = distantLayerX_A - scrollDistant;
    let newDistantX_B = distantLayerX_B - scrollDistant;
    if (newDistantX_A + screenWidth < 0) newDistantX_A = newDistantX_B + screenWidth;
    if (newDistantX_B + screenWidth < 0) newDistantX_B = newDistantX_A + screenWidth;
    setDistantLayerX_A(newDistantX_A);
    setDistantLayerX_B(newDistantX_B);

    const scrollClouds = SCROLL_SPEED_CLOUDS * delta;
    let newCloudX_A = cloudLayerX_A - scrollClouds;
    let newCloudX_B = cloudLayerX_B - scrollClouds;
    if (newCloudX_A + screenWidth < 0) newCloudX_A = newCloudX_B + screenWidth;
    if (newCloudX_B + screenWidth < 0) newCloudX_B = newCloudX_A + screenWidth;
    setCloudLayerX_A(newCloudX_A);
    setCloudLayerX_B(newCloudX_B);

    const scrollGround = SCROLL_SPEED_GROUND * delta; 
    let newGroundX_A = groundLayerX_A - scrollGround;
    let newGroundX_B = groundLayerX_B - scrollGround;
    if (newGroundX_A + screenWidth < 0) newGroundX_A = newGroundX_B + screenWidth;
    if (newGroundX_B + screenWidth < 0) newGroundX_B = newGroundX_A + screenWidth;
    setGroundLayerX_A(newGroundX_A);
    setGroundLayerX_B(newGroundX_B);
  });
  
  const drawSky = useCallback((g: PixiGraphicsClass) => {
    g.clear();
    g.rect(0, 0, screenWidth, screenHeight).fill({ color: SKY_COLOR }); 
  }, [screenWidth, screenHeight]);

  const drawDistantMountains = useCallback((g: PixiGraphicsClass) => { 
    g.clear();
    const groundY = screenHeight * 0.85; 
    
    let mountainBaseHeight = screenHeight * 0.20;
    let mountainWidth = screenWidth / 2.5;
    for (let i = 0; i < 4; i++) { 
      const x = i * mountainWidth * 0.7; 
      const h = mountainBaseHeight + (Math.sin(i * 1.2 + 0.5) * mountainBaseHeight * 0.4);
      g.poly([x, groundY, x + mountainWidth / 2, groundY - h, x + mountainWidth, groundY]).fill({color: MOUNTAIN_COLOR_FAR}); // ✨ Corrected to poly
    }
    mountainBaseHeight = screenHeight * 0.15;
    mountainWidth = screenWidth / 2;
     for (let i = 0; i < 4; i++) { 
      const x = i * mountainWidth * 0.75 + mountainWidth * 0.2; 
      const h = mountainBaseHeight + (Math.sin(i * 0.8) * mountainBaseHeight * 0.5);
      g.poly([x, groundY, x + mountainWidth / 2, groundY - h, x + mountainWidth, groundY]).fill({color: MOUNTAIN_COLOR_NEAR}); // ✨ Corrected to poly
    }
  }, [screenWidth, screenHeight]);

  const drawCloudLayer = useCallback((g: PixiGraphicsClass) => { 
    g.clear();
    for (let i = 0; i < 4; i++) {
      const clusterX = screenWidth * (0.1 + i * 0.28) + (Math.sin(i*2) * screenWidth * 0.05);
      const clusterY = screenHeight * (0.15 + (Math.sin(i * 1.5 + 1) * 0.08)); 
      const baseRadius = screenWidth * (0.06 + Math.sin(i*0.5)*0.02);
      
      g.ellipse(clusterX, clusterY, baseRadius * 1.2, baseRadius * 0.8).fill({color: CLOUD_COLOR_1, alpha: 0.65});
      g.ellipse(clusterX + baseRadius * 0.7, clusterY + baseRadius * 0.2, baseRadius, baseRadius * 0.7).fill({color: CLOUD_COLOR_1, alpha: 0.65});
      g.ellipse(clusterX - baseRadius * 0.6, clusterY + baseRadius * 0.3, baseRadius * 0.9, baseRadius * 0.6).fill({color: CLOUD_COLOR_1, alpha: 0.65});
      g.ellipse(clusterX + baseRadius * 0.2, clusterY + baseRadius * 0.4, baseRadius * 0.7, baseRadius * 0.5).fill({color: CLOUD_COLOR_2, alpha: 0.45});
    }
  }, [screenWidth, screenHeight]);

  const drawGroundLayer = useCallback((g: PixiGraphicsClass) => {
    g.clear();
    const groundTopY = screenHeight * 0.85; 
    const groundHeight = screenHeight - groundTopY;

    g.rect(0, groundTopY, screenWidth, groundHeight).fill(GROUND_COLOR_BASE); 

    const numLines = 5;
    for (let i = 1; i <= numLines; i++) {
      const lineY = groundTopY + (groundHeight * (i / (numLines + 1))) + (Math.sin(i*2) * 2 -1) ; 
      g.moveTo(0, lineY).lineTo(screenWidth, lineY).stroke({ width: 2, color: GROUND_COLOR_LINE, alpha: 0.6 });
    }
  }, [screenWidth, screenHeight]);

  return (
    <>
      <pixiGraphics draw={drawSky} zIndex={-100} />       
      <pixiGraphics draw={drawDistantMountains} x={distantLayerX_A} y={0} zIndex={-90} />
      <pixiGraphics draw={drawDistantMountains} x={distantLayerX_B} y={0} zIndex={-90} />
      <pixiGraphics draw={drawCloudLayer} x={cloudLayerX_A} y={0} zIndex={-80} />
      <pixiGraphics draw={drawCloudLayer} x={cloudLayerX_B} y={0} zIndex={-80} />
      <pixiGraphics draw={drawGroundLayer} x={groundLayerX_A} y={0} zIndex={-70} />
      <pixiGraphics draw={drawGroundLayer} x={groundLayerX_B} y={0} zIndex={-70} />
    </>
  );
};

export default BackgroundComponent;
// Rewrite Count: 8